import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as ImprovementIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { evaluationDatabaseApi } from '../../services/evaluationDatabaseApi';

const CourseAssistant = () => {
  const [activeTab, setActiveTab] = useState(0); // 0: 過往評估, 1: 改善項目細節
  const [loading, setLoading] = useState(false);
  
  // 功能1：改善動作相關聯的動作詳細
  const [improvementCategories, setImprovementCategories] = useState([]);
  const [selectedImprovement, setSelectedImprovement] = useState(null);
  const [relatedMovementDetails, setRelatedMovementDetails] = useState([]);
  
  // 功能2：查詢過往評估結果
  const [problemCategories, setProblemCategories] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [historicalEvaluations, setHistoricalEvaluations] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [viewMode, setViewMode] = useState('chips'); // 'chips' 或 'detailed'

  // 載入改善動作類別
  const loadImprovementCategories = async () => {
    try {
      setLoading(true);
      const response = await evaluationDatabaseApi.getImprovements();
      
      // 過濾出 category_type 為 'movement' 的改善項目
      const allImprovements = response || [];
      
      const movementImprovements = allImprovements.filter(improvement => {
        return improvement.category_type === 'movement';
      });
      
      // 按照指定的 skill_category 順序排序
      const skillCategoryOrder = [
        'position',
        'balancing', 
        'rotation',
        'counter_rotation',
        'inclination',
        'angulation',
        'reactive',
        'proactive',
        'timing',
        'coordination'
      ];
      
      const sortedImprovements = movementImprovements.sort((a, b) => {
        const aIndex = skillCategoryOrder.indexOf(a.skill_category);
        const bIndex = skillCategoryOrder.indexOf(b.skill_category);
        
        // 如果兩個都在順序列表中，按照順序排序
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        
        // 如果只有一個在順序列表中，在順序列表中的排在前面
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // 如果兩個都不在順序列表中，按照 skill_category 字母順序排序
        return (a.skill_category || '').localeCompare(b.skill_category || '');
      });
      
      setImprovementCategories(sortedImprovements);
    } catch (error) {
      console.error('載入改善項目失敗:', error);
      console.error('錯誤詳情:', error.response?.data);
      setImprovementCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // 載入問題動作類別
  const loadProblemCategories = async () => {
    try {
      setLoading(true);
      const response = await evaluationDatabaseApi.getProblemCategories();
      setProblemCategories(response || []);
    } catch (error) {
      console.error('載入問題動作類別失敗:', error);
      console.error('錯誤詳情:', error.response?.data);
      setProblemCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // 載入相關的動作詳細
  const loadRelatedMovementDetails = async (improvementId) => {
    try {
      setLoading(true);
      const response = await evaluationDatabaseApi.getImprovementMovementDetails(improvementId);
      setRelatedMovementDetails(response?.movement_details || []);
    } catch (error) {
      console.error('載入相關動作詳細失敗:', error);
      setRelatedMovementDetails([]);
    } finally {
      setLoading(false);
    }
  };

  // 查詢過往評估結果
  const searchHistoricalEvaluations = async (problemId) => {
    try {
      setLoading(true);
      const response = await evaluationDatabaseApi.searchEvaluationsByProblem(problemId);
      
      setHistoricalEvaluations(response?.evaluations || []);
      setSearchResults(response?.evaluations || []);
    } catch (error) {
      console.error('查詢過往評估結果失敗:', error);
      setHistoricalEvaluations([]);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 處理改善動作選擇
  const handleImprovementSelect = (improvement) => {
    setSelectedImprovement(improvement);
    if (improvement && improvement.id) {
      loadRelatedMovementDetails(improvement.id);
    } else {
      setRelatedMovementDetails([]);
    }
  };

  // 處理問題動作選擇
  const handleProblemSelect = (problem) => {
    setSelectedProblem(problem);
    if (problem && problem.id) {
      searchHistoricalEvaluations(problem.id);
    } else {
      setHistoricalEvaluations([]);
      setSearchResults([]);
    }
  };

  // 獲取不重複的改善動作及其計數，按計數從大到小排序
  const getUniqueImprovementMovements = () => {
    const movementCounts = new Map();
    searchResults.forEach(evaluation => {
      if (evaluation.improvement_movement) {
        const movement = evaluation.improvement_movement;
        movementCounts.set(movement, (movementCounts.get(movement) || 0) + 1);
      }
    });
    return Array.from(movementCounts.entries())
      .map(([movement, count]) => ({
        movement,
        count
      }))
      .sort((a, b) => b.count - a.count); // 按計數從大到小排序
  };

  // 處理 Chip 點擊，自動選擇改善動作並顯示動作細節
  const handleChipClick = async (movementName) => {
    // 在改善動作類別中查找匹配的項目
    const matchingImprovement = improvementCategories.find(
      improvement => improvement.category_name === movementName || improvement.name === movementName
    );
    
    if (matchingImprovement) {
      // 自動選擇該改善動作
      setSelectedImprovement(matchingImprovement);
      // 載入相關的動作細節
      await loadRelatedMovementDetails(matchingImprovement.id);
      // 切換到改善項目細節標籤（現在是索引1）
      setActiveTab(1);
    }
  };

  // 初始化載入
  useEffect(() => {
    // 檢查認證狀態
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    loadImprovementCategories();
    loadProblemCategories();
  }, []);

  // 功能1：改善動作與動作詳細關聯
  const renderImprovementMovementDetails = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                選擇改善動作
              </Typography>
              <Autocomplete
                options={(() => {
                  // 按技能類別分組的選項
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const improvements = improvementCategories.filter(
                      improvement => improvement.skill_category === skillCategory
                    );
                    
                    if (improvements.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的改善項目，按 effectiveness_level 然後 category_name 排序
                      improvements
                        .sort((a, b) => {
                          // 先按 effectiveness_level 排序（難度低的在前）
                          const levelA = a.effectiveness_level || 1;
                          const levelB = b.effectiveness_level || 1;
                          if (levelA !== levelB) {
                            return levelA - levelB;
                          }
                          // 難度相同時按 category_name 排序
                          return (a.category_name || a.name || '').localeCompare(b.category_name || b.name || '');
                        })
                        .forEach(improvement => {
                          groupedOptions.push({
                            type: 'option',
                            value: improvement,
                            label: improvement.category_name || improvement.name,
                            skillCategory: skillCategory,
                            improvementData: improvement
                          });
                        });
                    }
                  });
                  
                  // 添加沒有技能類別的項目
                  const uncategorizedImprovements = improvementCategories.filter(
                    improvement => !improvement.skill_category
                  );
                  
                  if (uncategorizedImprovements.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedImprovements
                      .sort((a, b) => (a.category_name || a.name || '').localeCompare(b.category_name || b.name || ''))
                      .forEach(improvement => {
                        groupedOptions.push({
                          type: 'option',
                          value: improvement,
                          label: improvement.category_name || improvement.name,
                          skillCategory: 'other',
                          improvementData: improvement
                        });
                      });
                  }
                  
                  return groupedOptions;
                })()}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option && typeof option === 'object') {
                    if (option.type === 'group') return option.label;
                    return option.label || '';
                  }
                  return '';
                }}
                value={selectedImprovement}
                onChange={(event, newValue) => {
                  // 只處理選項類型的選擇，忽略分組標題
                  if (newValue && typeof newValue === 'object' && newValue.type === 'option') {
                    handleImprovementSelect(newValue.value);
                  } else if (newValue && typeof newValue === 'object' && !newValue.type) {
                    // 直接選擇的改善項目對象
                    handleImprovementSelect(newValue);
                  }
                  // 如果是分組標題，不處理選擇
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="請選擇改善動作"
                    variant="outlined"
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  if (option && typeof option === 'object' && option.type === 'option') {
                    return option.value?.id === value?.id;
                  }
                  if (option && typeof option === 'object' && !option.type) {
                    return option.id === value?.id;
                  }
                  return false;
                }}
                getOptionDisabled={(option) => {
                  // 禁用分組標題
                  return option && typeof option === 'object' && option.type === 'group';
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) {
                    return options; // 如果沒有輸入，返回所有選項
                  }
                  
                  // 過濾選項，只顯示匹配的選項和它們的分組標題
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 先收集所有匹配的選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            py: 1,
                            px: 2
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  } else {
                    return (
                      <Box {...props} key={option.value?.id || option.label} sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                           - {option.improvementData?.effectiveness_level}級
                        </Typography>
                      </Box>
                    );
                  }
                }}
                loading={loading}
              />
              
              {selectedImprovement && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    選中的改善動作：
                  </Typography>
                  <Chip
                    label={selectedImprovement.category_name || selectedImprovement.name}
                    color="primary"
                    sx={{ 
                      mb: 1,
                      maxWidth: '100%',
                      height: 'auto',
                      minHeight: '32px',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        maxWidth: 'none',
                        display: 'block',
                        lineHeight: 1.2,
                        padding: '6px 12px',
                        height: 'auto',
                        minHeight: '20px'
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    技巧分類：{selectedImprovement.skill_category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    難度等級：{selectedImprovement.effectiveness_level}級
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                相關動作詳細
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress />
                </Box>
              ) : relatedMovementDetails.length > 0 ? (
                <List>
                  {relatedMovementDetails.map((detail, index) => (
                    <React.Fragment key={detail.id || index}>
                      <ListItem>
                        <ListItemText
                          primary={detail.name}
                          secondary={
                            <>
                              <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                描述：{detail.description}
                              </span>
                              <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                難度等級：{detail.difficulty_level}
                              </span>
                            </>
                          }
                        />
                      </ListItem>
                      {index < relatedMovementDetails.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  {selectedImprovement ? '此改善動作暫無相關的動作詳細' : '請先選擇改善動作'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // 功能2：查詢過往評估結果
  const renderHistoricalSearch = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                選擇問題動作
              </Typography>
              <Autocomplete
                options={(() => {
                  // 按技能類別分組的選項，過濾 category_type = movement
                  const skillCategoryOrder = [
                    'position', 'balancing', 'rotation', 'counter_rotation', 
                    'inclination', 'angulation', 'reactive', 'proactive', 
                    'timing', 'coordination'
                  ];
                  
                  const groupedOptions = [];
                  
                  // 為每個技能類別創建分組
                  skillCategoryOrder.forEach(skillCategory => {
                    const problems = problemCategories.filter(
                      problem => problem.skill_category === skillCategory && problem.category_type === 'movement'
                    );
                    
                    if (problems.length > 0) {
                      // 添加分組標題
                      const categoryLabel = {
                        'position': 'Position',
                        'balancing': 'Balancing',
                        'rotation': 'Rotation',
                        'counter_rotation': 'Counter Rotation',
                        'inclination': 'Inclination',
                        'angulation': 'Angulation',
                        'reactive': 'Reactive',
                        'proactive': 'Proactive',
                        'timing': 'Timing',
                        'coordination': 'Coordination'
                      }[skillCategory];
                      
                      groupedOptions.push({
                        type: 'group',
                        label: `--- ${categoryLabel} ---`,
                        skillCategory: skillCategory,
                        disabled: true
                      });
                      
                      // 添加該類別的問題項目，按 move_direction 然後 name 排序
                      problems
                        .sort((a, b) => {
                          // 先按 move_direction 排序
                          const directionA = a.move_direction || '';
                          const directionB = b.move_direction || '';
                          if (directionA !== directionB) {
                            return directionA.localeCompare(directionB);
                          }
                          // move_direction 相同時按 name 排序
                          return (a.name || '').localeCompare(b.name || '');
                        })
                        .forEach(problem => {
                          groupedOptions.push({
                            type: 'option',
                            value: problem,
                            label: problem.name,
                            skillCategory: skillCategory,
                            problemData: problem
                          });
                        });
                    }
                  });
                  
                  // 添加沒有技能類別的項目（但必須是 movement 類型）
                  const uncategorizedProblems = problemCategories.filter(
                    problem => !problem.skill_category && problem.category_type === 'movement'
                  );
                  
                  if (uncategorizedProblems.length > 0) {
                    groupedOptions.push({
                      type: 'group',
                      label: '--- 其他 ---',
                      skillCategory: 'other',
                      disabled: true
                    });
                    
                    uncategorizedProblems
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .forEach(problem => {
                        groupedOptions.push({
                          type: 'option',
                          value: problem,
                          label: problem.name,
                          skillCategory: 'other',
                          problemData: problem
                        });
                      });
                  }
                  
                  return groupedOptions;
                })()}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option && typeof option === 'object') {
                    if (option.type === 'group') return option.label;
                    return option.label || '';
                  }
                  return '';
                }}
                value={selectedProblem}
                onChange={(event, newValue) => {
                  // 只處理選項類型的選擇，忽略分組標題
                  if (newValue && typeof newValue === 'object' && newValue.type === 'option') {
                    handleProblemSelect(newValue.value);
                  } else if (newValue && typeof newValue === 'object' && !newValue.type) {
                    // 直接選擇的問題對象
                    handleProblemSelect(newValue);
                  }
                  // 如果是分組標題，不處理選擇
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="請選擇問題動作"
                    variant="outlined"
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  if (option && typeof option === 'object' && option.type === 'option') {
                    return option.value?.id === value?.id;
                  }
                  if (option && typeof option === 'object' && !option.type) {
                    return option.id === value?.id;
                  }
                  return false;
                }}
                getOptionDisabled={(option) => {
                  // 禁用分組標題
                  return option && typeof option === 'object' && option.type === 'group';
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) {
                    return options; // 如果沒有輸入，返回所有選項
                  }
                  
                  // 過濾選項，只顯示匹配的選項和它們的分組標題
                  const filtered = [];
                  const addedGroups = new Set();
                  
                  // 先收集所有匹配的選項
                  const matchingOptions = options.filter(option => 
                    option.type === 'option' && 
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  
                  // 為每個匹配的選項添加其分組標題（如果還沒添加）
                  matchingOptions.forEach(option => {
                    const groupOption = options.find(opt => 
                      opt.type === 'group' && 
                      opt.skillCategory === option.skillCategory
                    );
                    
                    if (groupOption && !addedGroups.has(groupOption.label)) {
                      filtered.push(groupOption);
                      addedGroups.add(groupOption.label);
                    }
                    
                    filtered.push(option);
                  });
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.type === 'group') {
                    return (
                      <Box 
                        {...props} 
                        key={option.label}
                        disabled={true}
                        sx={{ 
                          cursor: 'default !important',
                          backgroundColor: 'transparent !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'primary.main',
                            py: 1,
                            px: 2
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  } else {
                    return (
                      <Box {...props} key={option.value?.id || option.label} sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                           - {option.problemData?.move_direction}
                        </Typography>
                      </Box>
                    );
                  }
                }}
                loading={loading}
              />
              
              {selectedProblem && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    選中的問題動作：
                  </Typography>
                  <Chip
                    label={selectedProblem.name}
                    color="secondary"
                    sx={{ 
                      mb: 1,
                      maxWidth: '100%',
                      height: 'auto',
                      minHeight: '32px',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        maxWidth: 'none',
                        display: 'block',
                        lineHeight: 1.2,
                        padding: '6px 12px',
                        height: 'auto',
                        minHeight: '20px'
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    技巧分類：{selectedProblem.skill_category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    移動方向：{selectedProblem.move_direction}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                {/* 標題和重新查詢按鈕 */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: { xs: 2, sm: 1 }
                }}>
                  <Typography variant="subtitle1">
                    查詢結果 ({searchResults.length} 筆)
                  </Typography>
                  <Tooltip title={selectedProblem ? "重新查詢" : "請先選擇問題動作"}>
                    <span>
                      <IconButton 
                        onClick={() => selectedProblem && searchHistoricalEvaluations(selectedProblem.id)}
                        disabled={!selectedProblem}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                
                {/* 切換按鈕 */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}>
                  <Button
                    size="small"
                    variant={viewMode === 'chips' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('chips')}
                    sx={{ minWidth: '80px' }}
                  >
                    改善動作
                  </Button>
                  <Button
                    size="small"
                    variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('detailed')}
                    sx={{ minWidth: '80px' }}
                  >
                    詳細列表
                  </Button>
                </Box>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : searchResults.length > 0 ? (
                viewMode === 'chips' ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      找到 {getUniqueImprovementMovements().length} 種不同的改善動作：
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getUniqueImprovementMovements().map((item, index) => (
                        <Chip
                          key={index}
                          label={`${item.movement}  (${item.count}次)`}
                          color="primary"
                          variant="outlined"
                          clickable
                          onClick={() => handleChipClick(item.movement)}
                          sx={{ 
                            mb: 1,
                            maxWidth: '100%',
                            height: 'auto',
                            minHeight: '32px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            },
                            '& .MuiChip-label': {
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflow: 'visible',
                              textOverflow: 'unset',
                              maxWidth: 'none',
                              display: 'block',
                              lineHeight: 1.2,
                              padding: '6px 12px',
                              height: 'auto',
                              minHeight: '20px'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <List>
                    {searchResults.map((evaluation, index) => (
                      <React.Fragment key={evaluation.id || index}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <>
                                <Typography variant="subtitle1" component="span" sx={{ mr: 1 }}>
                                  {evaluation.student_name}
                                </Typography>
                                <Chip 
                                  label={evaluation.course_date} 
                                  size="small" 
                                  variant="outlined" 
                                  component="span"
                                />
                              </>
                            }
                            secondary={
                              <>
                                <span style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px' }}>
                                  問題動作：{evaluation.problem_movement}
                                </span>
                                <span style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px' }}>
                                  改善動作：{evaluation.improvement_movement}
                                </span>
                                <div style={{ display: 'block', fontSize: '0.875rem', marginBottom: '4px' }}>
                                  改善動作細節：
                                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                    {(() => {
                                      try {
                                        const details = typeof evaluation.improvement_movement_detail === 'string' 
                                          ? JSON.parse(evaluation.improvement_movement_detail)
                                          : evaluation.improvement_movement_detail;
                                        
                                        if (Array.isArray(details)) {
                                          return details.map((detail, index) => (
                                            <li key={index} style={{ marginBottom: '2px' }}>
                                              {detail}
                                            </li>
                                          ));
                                        } else if (details && typeof details === 'object') {
                                          return Object.values(details).map((detail, index) => (
                                            <li key={index} style={{ marginBottom: '2px' }}>
                                              {detail}
                                            </li>
                                          ));
                                        } else {
                                          return (
                                            <li style={{ marginBottom: '2px' }}>
                                              {evaluation.improvement_movement_detail}
                                            </li>
                                          );
                                        }
                                      } catch (error) {
                                        return (
                                          <li style={{ marginBottom: '2px' }}>
                                            {evaluation.improvement_movement_detail}
                                          </li>
                                        );
                                      }
                                    })()}
                                  </ul>
                                </div>
                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                                  建立時間：{new Date(evaluation.created_at).toLocaleString('zh-TW')}
                                </span>
                              </>
                            }
                          />
                        </ListItem>
                        {index < searchResults.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )
              ) : (
                <Alert severity="info">
                  {selectedProblem ? '未找到相關的評估記錄' : '請先選擇問題動作'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 主要內容區域 */}
      <Paper 
        elevation={8}
        sx={{ 
          p: 4,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.3)'
        }}
      >
        <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        🎿 課程輔助工具
        </Typography>
        
        <Tabs 
          value={activeTab} 
          onChange={(event, newValue) => setActiveTab(newValue)}
          centered
          sx={{ mb: 3 }}
        >
          <Tab label="過往評估" />
          <Tab label="改善項目細節" />
        </Tabs>
        
        {activeTab === 0 && renderHistoricalSearch()}
        {activeTab === 1 && renderImprovementMovementDetails()}
      </Paper>
    </Container>
  );
};

export default CourseAssistant;
